import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { DataService } from '@app/shared/services/data-service';
import { ProjectIndustryDTO } from '@app/shared/services/dtos/project-industry/project-industry.dto';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { ProjectIndustry } from '@app/shared/types/models/project-industry/project-industry.model';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { CompanyIndustriesDataService } from './company-industries-data.service';
import { ProjectIndustryFactory } from './factories/project-industry.factory';
import { UsersDataService } from './users-data.service';

@Injectable({ providedIn: 'root' })
export class ProjectIndustriesDataService extends DataService<ProjectIndustryDTO, ProjectIndustry> {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly industriesDataService: CompanyIndustriesDataService = inject(CompanyIndustriesDataService);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly projectIndustryFactory: ProjectIndustryFactory = inject(ProjectIndustryFactory);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly usersService: UsersDataService = inject(UsersDataService);

  private industries: DataStore<ProjectIndustry> = new DataStore<ProjectIndustry>();
  private projectIndustries: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  public getProjectIndustries(projectId: string): Signal<ProjectIndustry[] | undefined> {
    if (!projectId) {
      return signal(undefined);
    }
    if (!this.projectIndustries.hasDataForId(projectId)) {
      this.fetchProjectIndustries(projectId);
    }

    return computed(
      () =>
        this.projectIndustries
          .get(projectId)()
          ?.ids?.map(id => this.industries.get(id)()) || []
    );
  }

  public getById(industryId: string): Signal<ProjectIndustry | undefined> {
    return computed(() => this.industries.get(industryId)());
  }

  public getProjectIndustryByIndustryId(industryId: string, projectId: string): Signal<ProjectIndustry | undefined> {
    if (!industryId || !projectId) {
      return signal(undefined);
    }

    return computed(
      () => this.getProjectIndustries(projectId)()?.find(pi => pi.industry().id === industryId) || undefined
    );
  }

  // TODO: delete this and leave only get() methods that fetch data if it's not available
  public loadProjectIndustries(projectId: string): void {
    if (this.projectIndustries.hasDataForId(projectId)) {
      return;
    }
    this.fetchProjectIndustries(projectId);
  }

  public upsertLocalData(dto: ProjectIndustryDTO): Signal<ProjectIndustry> {
    return dto ? this.industries.upsert(this.projectIndustryFactory.produce(dto)) : signal(undefined);
  }

  protected fetchProjectIndustries(projectId: string): void {
    // this.loaderService.startLoading();

    this.httpClient.get<ProjectIndustryDTO[]>(environment.APIEndPoint + `projects/${projectId}/industries`).subscribe({
      next: (projectIndustries: ProjectIndustryDTO[]) => {
        if (projectIndustries.length) {
          projectIndustries.forEach(projectIndustry => {
            this.industriesDataService.upsertLocalData(projectIndustry.industry);
            this.usersService.upsertLocalData(projectIndustry.administrator);
          });
        }

        this.industries.upsertMany(projectIndustries.map(itm => this.projectIndustryFactory.produce(itm)));

        this.projectIndustries.upsert({
          id: projectId,
          ids: projectIndustries.map(itm => itm.id)
        });
      }
    });
  }

  public setIndustryAdmin(projectId: string, projectIndustry: ProjectIndustry, newAdminUserId: string): void {
    this.loaderService.startLoading();

    this.httpClient
      .put<ProjectIndustryDTO>(
        `${environment.APIEndPoint}projects/${projectId}/industries/${projectIndustry.industry().id}`,
        {
          industryId: projectIndustry.industry().id,
          administratorId: newAdminUserId
        }
      )
      .subscribe({
        next: (projectIndustryDTO: ProjectIndustryDTO) => {
          this.upsertLocalData(projectIndustryDTO);
          this.toastService.success(
            this.translateService.instant('project-industries.toasts.admin-successfully-changed')
          );
        },
        error: () => this.loaderService.stopLoading(),
        complete: () => this.loaderService.stopLoading()
      });
  }

  public deleteIndustry(projectId: string, projectIndustry: ProjectIndustry): void {
    this.loaderService.startLoading();

    this.httpClient
      .delete<void>(`${environment.APIEndPoint}projects/${projectId}/industries/${projectIndustry.industry().id}`)
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translateService.instant('project-industries.toasts.successfully-deleted', {
              name: projectIndustry.industry().name
            })
          );
          const existingProjectIndustries: string[] = this.projectIndustries.get(projectId)().ids ?? [];
          this.projectIndustries.upsert({
            id: projectId,
            ids: existingProjectIndustries.filter(id => id !== projectIndustry.id)
          });
          this.industries.delete(projectIndustry.id);
        },
        error: () => this.loaderService.stopLoading(),
        complete: () => this.loaderService.stopLoading()
      });
  }

  public addIndustryToProject(projectId: string, industryId: string, administratorId: string): void {
    this.loaderService.startLoading();

    this.httpClient
      .post<ProjectIndustryDTO>(`${environment.APIEndPoint}projects/${projectId}/industries/`, {
        industryId,
        administratorId
      })
      .subscribe({
        next: (projectIndustryDTO: ProjectIndustryDTO) => {
          this.upsertLocalData(projectIndustryDTO);
          this.toastService.success(
            this.translateService.instant('project-industries.toasts.successfully-deleted', {
              name: projectIndustryDTO.industry.name
            })
          );
          const existingProjectIndustries: string[] = this.projectIndustries.get(projectId)().ids ?? [];
          this.projectIndustries.upsert({
            id: projectId,
            ids: existingProjectIndustries.concat(projectIndustryDTO.id)
          });
        },
        error: () => this.loaderService.stopLoading(),
        complete: () => this.loaderService.stopLoading()
      });
  }
}
